import { Injectable } from '@angular/core';
import { PDFDocument } from 'pdf-lib';
import { Observable, of, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { StateFile } from 'src/app/enum/state-file.enum';
import { RegexUtil } from 'src/app/util/regex-util';
import { FileConfig } from '../model/file-config';
import { FileState } from '../model/file-state';
import { FileEnvelopeService } from './file-envelope.service';

const PDF = 'application/pdf';
let FORM;

@Injectable({
  providedIn: 'root'
})
export class FileValidateService {

  private subjectFileName$ = new Subject<FileState>();
  private subjectFileConfig$ = new Subject<FileState>();
  private subjectMergeFiles$ = new Subject<FileState>();
  private files: File[] = [];
  private _fileConfig: FileConfig[] = [];
  private _mergePDF: boolean;

  maxSizeAttachFiles = 70;
  maxSize: number = this.maxSizeAttachFiles * 1024 * 1024;

  constructor(
    private fileEnvelopeService: FileEnvelopeService
  ) { }

  get fileConfig(): FileConfig[] {
    return this._fileConfig;
  }
  set fileConfig(value: FileConfig[]) {
    this._fileConfig = value;
  }

  get mergePDF(): boolean {
    return this._mergePDF;
  }
  set mergePDF(value: boolean) {
    this._mergePDF = value;
  }


  getSubjectFileName(): Observable<FileState> {
    return this.subjectFileName$.asObservable();
  }

  setSubjectFileName(state: FileState) {
    this.subjectFileName$.next(state);
  }

  getSubjectFileConfig(): Observable<FileState> {
    return this.subjectFileConfig$.asObservable();
  }

  setSubjectFileConfig(state: FileState) {
    this.subjectFileConfig$.next(state);
  }

  getSubjectMergeFiles(): Observable<FileState> {
    return this.subjectMergeFiles$.asObservable();
  }

  setSubjectMergeFiles(state: FileState) {
    this.subjectMergeFiles$.next(state);
  }

  validFileName(files: File[]) {
    this.files = Object.assign([], files);
    const VALID_FILES: File[] = [];

    for (let index = 0; index < this.files.length; index++) {
      const isValid = new RegExp(RegexUtil.FILES_ENVELOPES, 'i').test(this.files[index].name);
      if (!isValid) {
        const fileState = new FileState();
        fileState.state = StateFile.INVALID_EXTENSION;
        fileState.file = this.files[index];
        this.setSubjectFileName(fileState);
        continue;
      }
      const hasDuplicate = this.files.filter(x => this.removeExt(x.name) === this.removeExt(this.files[index].name));
      if (hasDuplicate.length > 1) {
        const fileState = new FileState();
        fileState.state = StateFile.DUPLICATE;
        fileState.file = this.files[index];
        this.files.splice(index, 1);
        this.setSubjectFileName(fileState);
        continue;
      }
      VALID_FILES.push(this.files[index]);
    }
    if (VALID_FILES.length > 0) {
      const fileState = new FileState();
      fileState.state = StateFile.END_QUEUE_1;
      this.setSubjectFileConfig(fileState);
    } else {
      const fileState = new FileState();
      fileState.state = StateFile.FINNALY;
      this.setSubjectFileConfig(fileState);
    }
  }

  validFileConfig(files: File[]) {
    this.files = Object.assign([], files);

    this._fileConfig = [];

    for (let index = 0; index < this.files.length; index++) {
      const extensionFile = this.files[index].name.split('.').pop().toLowerCase();
      const sizeFile = this.files[index].size;
      let resultFile: File;
      if (extensionFile === 'doc' || extensionFile === 'docx' || extensionFile === 'rtf') {
        const fileFormData = new FormData();
        fileFormData.append('file', this.files[index], this.files[index].name);
        this.fileEnvelopeService
          .convertToPdf(fileFormData)
          .pipe(
            switchMap((ev: any) => {
              resultFile = this.convertBase64toFile('data:' + PDF + ';base64,' + ev.content, ev.name);
              return of(true);
            }),
          )
          .subscribe(x => {
            let fileState = new FileState();
            fileState.state = StateFile.CONVERTED;
            fileState.file = resultFile;
            this.setSubjectFileConfig(fileState);

            this.files[index] = resultFile;
            this.setFileConfig(this.files, index)
              .finally(() => {
                if ((index + 1) === this.files.length) {
                  fileState = new FileState();
                  fileState.state = StateFile.FINNALY;
                  this.setSubjectFileConfig(fileState);

                }
              });
          });
      } else {
        this.setFileConfig(this.files, index)
          .finally(() => {
            if ((index + 1) === this.files.length) {
              const fileState = new FileState();
              fileState.state = StateFile.FINNALY;
              this.setSubjectFileConfig(fileState);

            }
          });
      }

      if (sizeFile > this.maxSize) {
        const fileState = new FileState();
        fileState.state = StateFile.MAX_SIZE_LIMIT;
        this.setSubjectFileConfig(fileState);
      }
    }
  }

  removeExt(fileName: string) {
    return fileName.split('.').slice(0, -1).join('.');
  }

  convertBase64toFile(dataurl: string, filename: string) {
    const ARR = dataurl.split(',');
    const MIME = ARR[0].match(/:(.*?);/)[1];
    const BSTR = atob(ARR[1]);
    let n = BSTR.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = BSTR.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: MIME });
  }

  deleteFileConfig(fileName: string) {
    const files = this.fileConfig.filter(x => x.fileName !== fileName);
    this.fileConfig = files;
  }

  private async setFileConfig(files: File[], index: number) {
    const FILE = files[index];
    const BLOB = new Blob([FILE], { type: PDF });
    const PDF_URL = URL.createObjectURL(BLOB);
    const PDF_BYTES = await fetch(PDF_URL).then(res => res.arrayBuffer());
    let fileState = new FileState();
    await PDFDocument.load(PDF_BYTES, { ignoreEncryption: true })
      .then((value) => {
        const PDF_DOC = value;
        if (PDF_DOC.isEncrypted) {
          fileState.state = StateFile.ENCRYPTED;
          fileState.file = FILE;
          this.setSubjectFileConfig(fileState);
        } else {
          const PAGES = PDF_DOC.getPages();
          //const LAST_PAGE = PAGES[PAGES.length - 1];

          for (let i = 0; i < PAGES.length; i++) {
            const PAGE_ROTATION = PAGES[i].getRotation().angle == 90 || PAGES[i].getRotation().angle == 270;
            const WIDTH = PAGE_ROTATION ? PAGES[i].getHeight() : PAGES[i].getWidth();
            const HEIGHT = PAGE_ROTATION ? PAGES[i].getWidth() : PAGES[i].getHeight();
            const fileConfig = new FileConfig();
            fileConfig.length = PAGES.length;
            fileConfig.fileName = this.removeExt(FILE.name);
            fileConfig.height = Math.round(HEIGHT * 1.34);/**Ajustes calculos de posicionamento carimbos*/
            if (WIDTH <= 700) {
              fileConfig.width = Math.round(WIDTH * 1.34);
            } else if (WIDTH > 700 && WIDTH <= 1000) {
              fileConfig.width = Math.round(WIDTH * 1.310);
            } else if (WIDTH > 1000 && WIDTH <= 1180) {
              fileConfig.width = Math.round(WIDTH * 1.295);
            } else if (WIDTH > 1180 && WIDTH <= 1220) {
              fileConfig.width = Math.round(WIDTH * 1.270);
            } else if (WIDTH > 1220) {
              fileConfig.width = Math.round(WIDTH * 1.240);
            }
            const page = i + 1;
            fileConfig.page = page.toString();
            fileConfig.last = PAGES.length === page ? true : false;
            fileConfig.pageRotation = PAGE_ROTATION;
            this._fileConfig.push(fileConfig);
          }

          FORM = PDF_DOC.getForm();
          const FIELDS = FORM.getFields();
          let existsSignature = false;

          FIELDS.forEach(field => {
            try {
              FORM.getSignature(field.getName())
              existsSignature = true;
            } catch {
              field.enableReadOnly();
            }
          });

          if (!existsSignature) {
            PDF_DOC.save()
              .then((n) => {
                const DATA = new Blob([n], { type: 'application/pdf' });
                const ARRAY_OF_BLOB = new Array<Blob>();
                ARRAY_OF_BLOB.push(DATA);

                const NEW_FILE = new File(ARRAY_OF_BLOB, FILE.name, { type: PDF });
                fileState = new FileState();
                fileState.state = StateFile.CONVERTED;
                fileState.file = NEW_FILE;
                this.setSubjectFileConfig(fileState);
              });
          }

        }

      })
      .catch((error) => {
        fileState.state = StateFile.CORRUPTED;
        fileState.file = FILE;
        this.setSubjectFileConfig(fileState);
      });
  }

  async mergePDFDocuments() {
    const MERGED_PDF = await PDFDocument.create();

    let fileNames = '';
    this.files.forEach((x, index) => {
      if ((index + 1) < this.files.length) {
        fileNames = fileNames + this.removeExt(x.name) + ' - ';
      } else {
        fileNames = fileNames + this.removeExt(x.name) + '.pdf';
      }
    });

    for (let file of this.files) {

      const FILE = file;
      const BLOB = new Blob([FILE], { type: PDF });
      const PDF_URL = URL.createObjectURL(BLOB);
      const PDF_BYTES = await fetch(PDF_URL).then(res => res.arrayBuffer());
      const DOCUMENT = await PDFDocument.load(PDF_BYTES);

      const COPIED_PAGES = await MERGED_PDF.copyPages(DOCUMENT, DOCUMENT.getPageIndices());
      COPIED_PAGES.forEach((page) => MERGED_PDF.addPage(page));
    }

    const PDF_DATA_URI = await MERGED_PDF.saveAsBase64({
      dataUri: true
    });

    const MERGED_FILE = this.convertBase64toFile(PDF_DATA_URI, fileNames);

    let fileState = new FileState();
    fileState.state = StateFile.MERGED;
    fileState.file = MERGED_FILE
    this.setSubjectMergeFiles(fileState);

  }

}

